import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, ViewChild, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DashboardClaim } from '../../../core/services/dashboard.service';
import { I18nService } from '../../../core/services/i18n.service';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';

type ClaimDetailsTab = 'overview' | 'invoices' | 'titles' | 'payments';

@Component({
  selector: 'app-claim-overview',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './claim-overview.component.html',
  styleUrl: './claim-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClaimOverviewComponent implements OnChanges {
  private readonly i18n = inject(I18nService);
  private previouslyFocusedElement: HTMLElement | null = null;
  private returnFocusElement: HTMLElement | null = null;

  @ViewChild('detailsTrigger') private detailsTrigger?: ElementRef<HTMLButtonElement>;
  @ViewChild('detailsDialog') private detailsDialog?: ElementRef<HTMLElement>;
  @ViewChild('detailsCloseButton') private detailsCloseButton?: ElementRef<HTMLButtonElement>;

  @Input({ required: true }) claim!: DashboardClaim;
  @Output() readonly payNow = new EventEmitter<void>();

  readonly showMainClaimDetails = signal(false);
  readonly showInstallments = signal(false);
  readonly detailsOpen = signal(false);
  readonly activeDetailsTab = signal<ClaimDetailsTab>('overview');

  ngOnChanges(): void {
    if (!this.detailTabs().includes(this.activeDetailsTab())) {
      this.activeDetailsTab.set('overview');
    }
  }

  get totalClaim(): number {
    return this.claim.breakdown.titleAware
      ? this.claim.outstandingAmount
      : this.claim.breakdown.mainClaim + this.claim.breakdown.interest + this.claim.breakdown.costs;
  }

  triggerPayNow(): void {
    this.payNow.emit();
  }

  toggleInstallments(): void {
    this.showInstallments.update((open) => !open);
  }

  toggleMainClaimDetails(): void {
    this.showMainClaimDetails.update((open) => !open);
  }

  openDetails(returnFocusTo: HTMLElement | null = null): void {
    this.previouslyFocusedElement = this.currentFocusedElement();
    this.returnFocusElement = returnFocusTo;
    this.activeDetailsTab.set(this.detailTabs()[0]);
    this.detailsOpen.set(true);
    this.focusDetailsDialog();
  }

  closeDetails(): void {
    if (!this.detailsOpen()) {
      return;
    }

    this.detailsOpen.set(false);
    this.restoreDetailsFocus();
  }

  activateDetailsTab(tab: ClaimDetailsTab): void {
    this.activeDetailsTab.set(tab);
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.detailsOpen()) {
      return;
    }

    if (event.key === 'Escape') {
      this.closeDetails();
      return;
    }

    if (event.key === 'Tab') {
      this.trapDetailsFocus(event);
    }
  }

  detailTabs(): ClaimDetailsTab[] {
    return ['overview', this.claim.breakdown.titleDetails.length > 0 ? 'titles' : 'invoices', 'payments'];
  }

  detailTabLabel(tab: ClaimDetailsTab): string {
    return this.i18n.t(`claim.detailsTabs.${tab}`);
  }

  formatCurrency(amount: number): string {
    return this.i18n.formatCurrency(amount);
  }

  formatDate(date: string): string {
    if (!date) {
      return this.i18n.t('claim.notAvailable');
    }

    return this.i18n.formatDate(date);
  }

  invoicePresentationText(field: 'sectionLabel' | 'amountLabel' | 'note'): string {
    const presentation = this.claim.breakdown.invoicePresentation;
    const key = presentation[`${field}Key`];
    return key ? this.i18n.t(key) : presentation[field];
  }

  mainClaimLabel(): string {
    return this.i18n.t(this.claim.breakdown.mainClaimLabelKey || 'claim.mainClaim');
  }

  detailedAmount(row: { amount: number | null; amountText: string }): string {
    if (row.amount !== null) {
      return this.formatCurrency(row.amount);
    }

    return row.amountText || '-';
  }

  hasDetailedClaimColumns(): boolean {
    return this.claim.breakdown.detailedStatement.some((row) => row.hasStructuredDetails);
  }

  detailedDate(date: string): string {
    if (!date) {
      return '-';
    }

    return this.formatDate(date);
  }

  detailedText(value: string | null | undefined): string {
    return value || '-';
  }

  detailedValue(amount: number | null | undefined, amountText: string | undefined): string {
    if (amount !== null && amount !== undefined) {
      return this.formatCurrency(amount);
    }

    return amountText || '-';
  }

  private focusDetailsDialog(): void {
    window.setTimeout(() => {
      if (!this.detailsOpen()) {
        return;
      }

      const firstFocusable = this.focusableDetailsElements()[0];
      (this.detailsCloseButton?.nativeElement ?? firstFocusable ?? this.detailsDialog?.nativeElement)?.focus();
    });
  }

  private trapDetailsFocus(event: KeyboardEvent): void {
    const focusableElements = this.focusableDetailsElements();
    const firstFocusable = focusableElements[0] ?? this.detailsDialog?.nativeElement;
    const lastFocusable = focusableElements[focusableElements.length - 1] ?? this.detailsDialog?.nativeElement;

    if (!firstFocusable || !lastFocusable) {
      event.preventDefault();
      return;
    }

    const activeElement = this.currentFocusedElement();
    const dialog = this.detailsDialog?.nativeElement;

    if (!activeElement || !dialog?.contains(activeElement)) {
      event.preventDefault();
      (event.shiftKey ? lastFocusable : firstFocusable).focus();
      return;
    }

    if (event.shiftKey && activeElement === firstFocusable) {
      event.preventDefault();
      lastFocusable.focus();
      return;
    }

    if (!event.shiftKey && activeElement === lastFocusable) {
      event.preventDefault();
      firstFocusable.focus();
    }
  }

  private focusableDetailsElements(): HTMLElement[] {
    const dialog = this.detailsDialog?.nativeElement;
    if (!dialog) {
      return [];
    }

    return Array.from(dialog.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    )).filter((element) => element.tabIndex >= 0 && element.getAttribute('aria-hidden') !== 'true');
  }

  private currentFocusedElement(): HTMLElement | null {
    const activeElement = document.activeElement;
    return activeElement instanceof HTMLElement ? activeElement : null;
  }

  private restoreDetailsFocus(): void {
    const target = this.returnFocusElement?.isConnected ? this.returnFocusElement : this.detailsTrigger?.nativeElement;

    if (target?.isConnected) {
      target.focus();
    } else if (this.previouslyFocusedElement?.isConnected) {
      this.previouslyFocusedElement.focus();
    }

    this.previouslyFocusedElement = null;
    this.returnFocusElement = null;
  }
}
